import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Heart, AlertTriangle, MessageCircle, Zap, Target, RefreshCw, Sparkles, Check } from 'lucide-react';
import useTranslation from '../data/useTranslation';
import LanguageSelector from '../components/LanguageSelector';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function DuoReport() {
  const { duoLinkId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const td = t.duo;

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchReport = async () => {
    const token = localStorage.getItem('bpm_access_token');
    if (!token) { navigate('/login'); return; }

    try {
      setLoading(true);
      const res = await fetch(`${API}/api/duo/compatibility/${duoLinkId}?lang=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error');
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    const token = localStorage.getItem('bpm_access_token');
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/duo/compatibility/${duoLinkId}/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => { fetchReport(); }, [duoLinkId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">{t.common.error}</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="px-6 py-3 bg-orange-500 text-white rounded-lg font-medium">{td.backToDashboard}</button>
        </div>
      </div>
    );
  }

  // Pending states
  if (data?.status === 'pending' || data?.status === 'incomplete') {
    return (
      <div className="min-h-screen bg-[#111827]">
        <header className="border-b border-gray-800 bg-[#111827]/95 backdrop-blur-md sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" /><span>{td.backToDashboard}</span>
            </button>
            <LanguageSelector />
          </div>
        </header>
        <div className="container mx-auto px-6 py-16 max-w-2xl">
          <div className="text-center">
            <div className="w-24 h-24 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-12 h-12 text-purple-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">{td.waitingPartner}</h1>
            <p className="text-gray-400 mb-8">{data?.message || td.waitingPartnerDesc}</p>

            {data?.status === 'incomplete' && (
              <div className="bg-[#1F2937] rounded-xl p-6 border border-gray-700 inline-block">
                <p className="text-gray-400 text-sm mb-2">{td.partnerProgress}</p>
                <div className="flex items-center gap-4">
                  <span className="text-white font-bold">{data?.user1_progress || 0}/6</span>
                  <div className="w-32 h-2 bg-gray-700 rounded-full"><div className="h-full bg-purple-500 rounded-full" style={{width: `${((data?.user1_progress || 0)/6)*100}%`}} /></div>
                  <span className="text-white font-bold">{data?.user2_progress || 0}/6</span>
                </div>
                <p className="text-gray-500 text-xs mt-2">{td.testsNeeded}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Report ready
  const report = data?.report;

  return (
    <div className="min-h-screen bg-[#111827]">
      <header className="border-b border-gray-800 bg-[#111827]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-gray-400 hover:text-white" data-testid="duo-back-btn">
            <ArrowLeft className="w-5 h-5" /><span>{td.backToDashboard}</span>
          </button>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <button onClick={handleRegenerate} disabled={generating} className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm transition-colors" data-testid="duo-regenerate-btn">
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {generating ? td.generating : td.regenerate}
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full text-purple-400 text-sm mb-4">
            <Sparkles className="w-4 h-4" /><span>{td.duoOffer}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="duo-report-title">{td.title}</h1>
          <p className="text-gray-400">{td.subtitle}</p>
          {data?.generated_at && (
            <p className="text-gray-600 text-sm mt-2">{new Date(data.generated_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          )}
        </div>

        {report ? (
          <div className="space-y-8">
            {/* Global Analysis */}
            {report.analyse_globale && (
              <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 rounded-2xl p-6 border border-purple-500/30" data-testid="duo-global-analysis">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Target className="w-6 h-6 text-purple-400" />{td.globalAnalysis}
                </h2>
                <p className="text-gray-300 leading-relaxed">{report.analyse_globale}</p>
              </div>
            )}

            {/* Compatibility Score */}
            {report.score_compatibilite && (
              <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700 text-center">
                <h2 className="text-lg font-bold text-white mb-4">{td.compatibilityScore}</h2>
                <div className="w-32 h-32 mx-auto relative">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="64" cy="64" r="56" fill="none" stroke="#374151" strokeWidth="8" />
                    <circle cx="64" cy="64" r="56" fill="none" stroke="#A855F7" strokeWidth="8"
                      strokeDasharray={`${(report.score_compatibilite / 100) * 352} 352`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-purple-400">{report.score_compatibilite}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Complementarity */}
            {report.complementarite && (
              <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700" data-testid="duo-complementarity">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Heart className="w-6 h-6 text-pink-400" />{td.complementarity}
                </h2>
                {Array.isArray(report.complementarite) ? (
                  <ul className="space-y-3">
                    {report.complementarite.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 bg-[#111827] rounded-lg">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{typeof item === 'string' ? item : item.point || item.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-300">{report.complementarite}</p>
                )}
              </div>
            )}

            {/* Tensions */}
            {report.tensions && (
              <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700" data-testid="duo-tensions">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-amber-400" />{td.tensions}
                </h2>
                {Array.isArray(report.tensions) ? (
                  <ul className="space-y-3">
                    {report.tensions.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 bg-[#111827] rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{typeof item === 'string' ? item : item.point || item.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-300">{report.tensions}</p>
                )}
              </div>
            )}

            {/* Communication Tips */}
            {report.conseils_communication && (
              <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700" data-testid="duo-communication">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <MessageCircle className="w-6 h-6 text-blue-400" />{td.communicationTips}
                </h2>
                {Array.isArray(report.conseils_communication) ? (
                  <ul className="space-y-3">
                    {report.conseils_communication.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 bg-[#111827] rounded-lg">
                        <MessageCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{typeof item === 'string' ? item : item.conseil || item.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-300">{report.conseils_communication}</p>
                )}
              </div>
            )}

            {/* Combined Strengths */}
            {report.forces_combinees && (
              <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700" data-testid="duo-strengths">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-orange-400" />{td.combinedStrengths}
                </h2>
                {Array.isArray(report.forces_combinees) ? (
                  <ul className="space-y-3">
                    {report.forces_combinees.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 bg-[#111827] rounded-lg">
                        <Zap className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{typeof item === 'string' ? item : item.force || item.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-300">{report.forces_combinees}</p>
                )}
              </div>
            )}

            {/* Action Plan */}
            {report.plan_action && (
              <div className="bg-gradient-to-br from-orange-900/30 to-purple-900/30 rounded-2xl p-6 border border-orange-500/30" data-testid="duo-action-plan">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Target className="w-6 h-6 text-orange-400" />{td.actionPlan}
                </h2>
                {Array.isArray(report.plan_action) ? (
                  <ul className="space-y-3">
                    {report.plan_action.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 bg-[#111827]/50 rounded-lg">
                        <span className="w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{i + 1}</span>
                        <span className="text-gray-300">{typeof item === 'string' ? item : item.action || item.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-300">{report.plan_action}</p>
                )}
              </div>
            )}

            {/* Raw report fallback for unknown structures */}
            {!report.complementarite && !report.tensions && !report.analyse_globale && (
              <div className="bg-[#1F2937] rounded-2xl p-6 border border-gray-700">
                <h2 className="text-xl font-bold text-white mb-4">{td.title}</h2>
                <pre className="text-gray-300 whitespace-pre-wrap text-sm">{JSON.stringify(report, null, 2)}</pre>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400">{t.common.error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
